import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useStoreCount } from "@/hooks/useStoreCount";
import { ScaleAndSpeed, DealersFirst, MarketingGrowth } from "@/components/shared/DealerAdvantage";
import {
  ClipboardList, UserCheck, FileSignature, Rocket, Headphones,
  Award, ChevronRight, Phone, Mail,
} from "lucide-react";

/**
 * Dense SEO-targeted content block appended to /totalwirelessmasteragent,
 * /open-a-store, and every /:stateSlug page. Purpose: rank top-5 for
 *   1. "how to become total wireless dealer"
 *   2. "become a total wireless exclusive retailer"
 *
 * If `stateName` is provided, all copy + FAQs + schema are localised to
 * "in {stateName}" so the same block ranks for state-modified queries
 * (e.g. "how to become total wireless dealer in ohio").
 *
 * Injects HowTo + FAQPage JSON-LD inline so the page qualifies for rich
 * results regardless of the global LocalBusiness schema above it.
 */
export function SEOExpansion({ stateName = "", withAdvantage = true }) {
  const storeCount = useStoreCount();
  const statePhrase = stateName ? ` in ${stateName}` : "";
  const stateShort = stateName ? ` — ${stateName}` : "";
  const stateLc = stateName ? ` in ${stateName.toLowerCase()}` : "";

  const steps = [
    {
      icon: ClipboardList,
      name: "Apply Online",
      text: `Submit the Prime Media Wireless dealer application${statePhrase}. It takes about 5 minutes — we only need your business details, store location${statePhrase}, and retail experience.`,
    },
    {
      icon: UserCheck,
      name: "Qualify & Interview",
      text: `Our team reviews your application and schedules a short interview. We look for dedication and a location${statePhrase} that serves your local community well — no franchise fee and no prior wireless experience required.`,
    },
    {
      icon: FileSignature,
      name: "Sign the Exclusive Retailer Agreement",
      text: `Once approved you sign the Total Wireless Exclusive Retailer Agreement, gain access to the dealer portal, and receive your authorized dealer number. We handle the paperwork with Verizon on your behalf.`,
    },
    {
      icon: Rocket,
      name: "Launch Your Store",
      text: `Receive your initial inventory, signage, and point-of-sale setup${statePhrase}. Our onboarding team guides you through go-live day so you can start activating customers on the Verizon 5G network from day one.`,
    },
    {
      icon: Headphones,
      name: "Grow with Ongoing Support",
      text: `Dedicated account manager, weekly training, merchandising updates, marketing co-op dollars, and commission payouts. We help you scale to multiple Total Wireless stores${statePhrase} when you're ready.`,
    },
  ];

  const faqs = [
    {
      q: `How do I become a Total Wireless dealer${statePhrase}?`,
      a: `Apply online through Prime Media Wireless, complete a short interview, sign the Total Wireless Exclusive Retailer Agreement, and launch your store${statePhrase}. Prime Media Wireless is a premier Total Wireless Master Agent covered by Verizon, and we handle the full application process with Verizon on your behalf.`,
    },
    {
      q: `What are the requirements to become a Total Wireless Exclusive Retailer${statePhrase}?`,
      a: `You need a qualified retail location (min. ~400 sq ft), a business license, EIN, and commitment to Total Wireless exclusivity during the term. Prior wireless experience is NOT required${stateLc} — we provide the full training curriculum.`,
    },
    {
      q: `How much does it cost to open a Total Wireless store${statePhrase}?`,
      a: `There is no franchise fee. Typical startup costs${statePhrase} range $15,000–$40,000 covering inventory, fixtures, signage, and initial marketing, depending on store size. We help you budget realistically during the onboarding call.`,
    },
    {
      q: `How long does it take to open a Total Wireless store${statePhrase}?`,
      a: `Setup typically takes 30–35 days${statePhrase} with our step-by-step launch process: application review, agreement signing, then inventory and build-out depending on location readiness. Ready-to-go stores can open even faster.`,
    },
    {
      q: `What's the difference between a Total Wireless dealer and a Total Wireless Exclusive Retailer?`,
      a: `An Exclusive Retailer is a dedicated Total Wireless storefront that carries Total Wireless / Verizon Value products exclusively. "Dealer" is used interchangeably. Prime Media Wireless is the Master Agent that sponsors and supports Exclusive Retailers${statePhrase} nationwide.`,
    },
    {
      q: `Does Prime Media Wireless help with marketing${statePhrase}?`,
      a: `Yes. We provide marketing co-op funds, professionally designed social-media creative, local SEO support${statePhrase}, Google Business Profile optimization, and participation in Verizon national campaigns.`,
    },
    {
      q: `What commissions and incentives do Total Wireless dealers earn${statePhrase}?`,
      a: `Dealers earn activation commissions on new lines, residuals on ongoing accounts, accessory margin, device protection commissions, and hit-the-number bonuses paid monthly. We'll walk you through the full incentive structure during your discovery call.`,
    },
    {
      q: `Can I open multiple Total Wireless stores${statePhrase}?`,
      a: `Absolutely. Many Prime Media Wireless dealers grow from a single location to 3-10+ stores${statePhrase}. Our multi-store operators get dedicated support, bulk inventory pricing, and territory-expansion consulting.`,
    },
    {
      q: `Is Total Wireless the same as Total by Verizon${statePhrase}?`,
      a: `Yes. Total Wireless was rebranded as Total by Verizon, and both names refer to the same prepaid wireless service on Verizon's 5G network. The dealer sign-up process, Exclusive Retailer Agreement, commissions, and store opening process are identical whether you search for "how to become a Total Wireless dealer" or "how to become a Total by Verizon dealer" — Prime Media Wireless is the authorized Master Agent for both brand names.`,
    },
    {
      q: `What is the Total Wireless franchise cost${statePhrase}?`,
      a: `There is no Total Wireless (or Total by Verizon) franchise fee. The program operates as an Exclusive Retailer model, not a franchise. Typical startup investment${statePhrase} of $15,000–$40,000 covers inventory, fixtures, signage, and launch marketing — significantly less than the $75,000–$250,000+ typical for wireless franchise models.`,
    },
  ];

  // HowTo schema (Google rich result eligible)
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to Become a Total Wireless Dealer${statePhrase}`,
    "description": `Step-by-step guide to becoming a Total Wireless Exclusive Retailer${statePhrase} with Prime Media Wireless, the Master Agent covered by Verizon.`,
    "totalTime": "P45D",
    "step": steps.map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.name,
      "text": s.text,
    })),
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <>
      {withAdvantage && <ScaleAndSpeed />}
      {withAdvantage && <DealersFirst />}
      {withAdvantage && <MarketingGrowth />}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* HOW-TO SECTION */}
      <section className="bg-white py-16 lg:py-20" data-testid="seo-howto-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/10 text-teal text-xs font-semibold uppercase tracking-wider">
              <Award className="w-3.5 h-3.5" /> Step-by-Step Guide
            </span>
            <h2 className="font-heading font-bold text-3xl lg:text-5xl text-navy mt-4 mb-3">
              How to Become a Total Wireless Dealer{stateShort}
            </h2>
            <p className="text-slate-600 font-body max-w-3xl mx-auto">
              Launching a Total Wireless store{statePhrase} takes 30–35 days from application to grand opening. Prime Media Wireless — a premier Total Wireless Master Agent covered by Verizon — walks you through every step.
            </p>
          </div>

          <ol className="space-y-6" data-testid="seo-howto-steps">
            {steps.map((s, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex gap-5 bg-slate-50 border border-slate-100 rounded-2xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-teal text-white flex items-center justify-center shadow-md">
                  <s.icon className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-xs font-semibold text-teal uppercase tracking-wider">Step {i + 1}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-navy mb-2">{s.name}</h3>
                  <p className="text-slate-600 font-body leading-relaxed">{s.text}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* EXCLUSIVE RETAILER SECTION */}
      <section className="bg-navy py-16 lg:py-20 text-white" data-testid="seo-exclusive-retailer-section">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/20 text-teal-light text-xs font-semibold uppercase tracking-wider">
              <Award className="w-3.5 h-3.5" /> Exclusive Retailer Program
            </span>
            <h2 className="font-heading font-bold text-3xl lg:text-5xl mt-4 mb-5">
              Become a Total Wireless Exclusive Retailer{stateShort}
            </h2>
            <div className="space-y-4 text-slate-200 font-body leading-relaxed">
              <p>
                The <strong>Total Wireless Exclusive Retailer Program</strong> is Verizon's authorized retail channel for Total Wireless and Verizon Value products. As an Exclusive Retailer{statePhrase}, your storefront carries the Total Wireless brand exclusively — unlocking the highest commission tiers, direct Verizon device inventory, and co-branded marketing support.
              </p>
              <p>
                Prime Media Wireless is the <strong>premier Total Wireless Master Agent</strong> recruiting, onboarding, and supporting Exclusive Retailers nationwide{stateShort ? ` — including ${stateName}` : ""}. With 30+ years of experience in wireless retail operations and {storeCount}+ active stores across the country, we know exactly what it takes to run a profitable Total Wireless location.
              </p>
              <p>
                <strong>Why the Exclusive Retailer model wins{statePhrase}:</strong> better device margins, protected territory, residual income, training, and a direct line to the Verizon regional team. It's the clearest path to growing from a single location into a multi-store wireless business{statePhrase}.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/become-dealer#apply"
                className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white px-6 py-3 rounded-full font-semibold transition-colors"
                data-testid="seo-become-dealer-cta"
              >
                Start Your Total Wireless Dealer Sign-Up <ChevronRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+12485900000"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-full font-semibold transition-colors"
              >
                <Phone className="w-4 h-4" /> (248) 590-0000
              </a>
            </div>
          </div>

          <div className="space-y-4" data-testid="seo-exclusive-retailer-benefits">
            {[
              { t: "Highest Commissions", d: "Top-tier activation payouts + residuals unavailable to non-exclusive dealers." },
              { t: "Protected Territory", d: `Lock in your coverage area${statePhrase} so competing Total Wireless stores don't open next door.` },
              { t: "Direct Verizon Inventory", d: "Apple, Samsung, Google, Motorola devices shipped fast at dealer pricing." },
              { t: "Full-Funnel Marketing", d: "Google Business Profile optimization, social creative, co-op ad dollars." },
              { t: "Multi-Store Path", d: `Grow from 1 to 10+ Total Wireless stores${statePhrase} with dedicated expansion support.` },
              { t: "Master Agent Backing", d: "Prime Media Wireless handles every Verizon escalation — you focus on sales." },
            ].map((b, i) => (
              <div key={i} className="flex gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-teal/20 flex items-center justify-center">
                  <ChevronRight className="w-5 h-5 text-teal-light" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-white">{b.t}</div>
                  <div className="text-sm text-slate-300 mt-0.5">{b.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="bg-slate-50 py-16 lg:py-20" data-testid="seo-faq-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-navy/10 text-navy text-xs font-semibold uppercase tracking-wider">
              FAQs
            </span>
            <h2 className="font-heading font-bold text-3xl lg:text-5xl text-navy mt-4 mb-3">
              Total Wireless Dealer Questions{stateShort}
            </h2>
            <p className="text-slate-600 font-body">
              Everything aspiring Total Wireless Exclusive Retailers{statePhrase} ask us.
            </p>
          </div>

          <div className="space-y-3" data-testid="seo-faqs">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <h3 className="font-heading font-semibold text-navy text-lg">{f.q}</h3>
                  <ChevronRight className="w-5 h-5 text-teal flex-shrink-0 mt-1 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-slate-600 font-body leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>

          <div className="mt-10 bg-navy text-white rounded-2xl p-8 text-center">
            <h3 className="font-heading font-bold text-2xl mb-2">
              Ready to open your Total Wireless store{statePhrase}?
            </h3>
            <p className="text-slate-300 mb-5">
              Talk to a real person at Prime Media Wireless. No sales pitch — just straight answers.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to="/become-dealer#apply"
                className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white px-6 py-3 rounded-full font-semibold transition-colors"
                data-testid="seo-faq-apply-cta"
              >
                Start Your Dealer Application <ChevronRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:info@primemediawireless.com"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-full font-semibold transition-colors"
              >
                <Mail className="w-4 h-4" /> info@primemediawireless.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* EXACT-MATCH KEYWORD SECTION — targets 20+ long-tail queries from Search Console */}
      <section className="bg-white py-16 lg:py-20" data-testid="seo-query-match-section">
        <div className="max-w-6xl mx-auto px-4 space-y-12">

          {/* Sign-up / application */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Total Wireless Dealer Sign-Up{stateShort}
            </h2>
            <p className="text-slate-700 font-body leading-relaxed mb-4">
              The Total Wireless dealer sign-up{statePhrase} is handled end-to-end by Prime Media Wireless — the authorized Total Wireless Master Agent covered by Verizon. Submit the Total Wireless dealer sign-up form, we review, sign the Exclusive Retailer Agreement, and you're a Total Wireless retailer{statePhrase} in as little as 30–35 days.
            </p>
            <Link
              to="/become-dealer#apply"
              className="inline-flex items-center gap-2 text-teal font-semibold hover:underline"
              data-testid="seo-signup-anchor"
            >
              Open the Total Wireless Dealer Sign-Up Form <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Qualifications */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Qualifications to Become a Total Wireless Retailer{stateShort}
            </h2>
            <ul className="text-slate-700 font-body leading-relaxed space-y-2 list-disc pl-5">
              <li>A qualified retail storefront{statePhrase} (minimum ~400 sq ft of showroom space).</li>
              <li>Valid business license + EIN for the operating entity.</li>
              <li>Commitment to the Total Wireless Exclusive Retailer model during your agreement term.</li>
              <li>Basic retail operations capability — prior wireless experience is NOT required{stateLc}.</li>
              <li>Working capital of roughly $15,000–$40,000 to cover inventory, fixtures, and launch marketing.</li>
            </ul>
          </div>

          {/* Benefits */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Benefits for Total Wireless Dealers & Retailers{stateShort}
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-slate-700 font-body">
              <p><strong>High-margin commissions.</strong> Top-tier activation payouts plus residuals on every line, paid monthly.</p>
              <p><strong>Device financing programs.</strong> Access to Glow Financial Services + Total Wireless Protect to close more sales.</p>
              <p><strong>Protected territory.</strong> Lock in your coverage area{statePhrase} so competing Total Wireless stores don't open next door.</p>
              <p><strong>Marketing co-op.</strong> Professionally produced ad creative, Google Business Profile optimization, and shared ad budgets.</p>
              <p><strong>Dedicated account manager.</strong> A real human handling every Verizon escalation so you stay on the sales floor.</p>
              <p><strong>Multi-store expansion path.</strong> Bulk inventory pricing + territory consulting when you're ready to scale beyond one store{statePhrase}.</p>
            </div>
          </div>

          {/* Franchise clarification + cost */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Total Wireless Franchise Cost & Fee Structure
            </h2>
            <p className="text-slate-700 font-body leading-relaxed mb-3">
              The most common question we get is about <strong>Total Wireless franchise cost</strong> (also searched as "Total by Verizon franchise cost"). Here's the straight answer: Total Wireless is <strong>not a traditional franchise</strong>. There is <strong>no franchise fee, no royalty on sales, and no long-term franchise disclosure document</strong> to sign. Instead, you operate as an authorized <strong>Total Wireless Exclusive Retailer</strong> under the Total Wireless (Total by Verizon) brand through Prime Media Wireless.
            </p>
            <p className="text-slate-700 font-body leading-relaxed">
              Typical startup investment{statePhrase} for opening a Total Wireless store runs <strong>$15,000 – $40,000</strong> covering inventory, store fixtures, signage, point-of-sale system, and initial marketing. That's dramatically lower than a typical wireless franchise cost of $75,000–$250,000+. The dealer / exclusive retailer model is faster, cheaper, and far less restrictive than franchising — you keep 100% of the economics while getting the brand equity, device inventory, and commission structure of the Verizon 5G network.
            </p>
          </div>

          {/* Total by Verizon rebrand coverage */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Total Wireless &amp; Total by Verizon — Same Brand, Same Dealer Program
            </h2>
            <p className="text-slate-700 font-body leading-relaxed">
              Total Wireless was rebranded as <strong>Total by Verizon</strong> and both names refer to the same prepaid wireless service on Verizon's 5G network. If you're searching for an <strong>authorized Total by Verizon master agent</strong>, a <strong>premier Total by Verizon master agent</strong>, or guidance on <strong>how to become a Total by Verizon dealer</strong> or <strong>how to open a Total by Verizon store</strong>, you're in the right place — Prime Media Wireless is the same Master Agent that recruits, onboards, and supports Total Wireless / Total by Verizon Exclusive Retailers nationwide. The dealer sign-up process, commission structure, and store opening process are identical under either brand name.
            </p>
          </div>

          {/* Master agent / USA — expanded to cover generic queries */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Premier Total Wireless Master Agent in the USA
            </h2>
            <p className="text-slate-700 font-body leading-relaxed mb-3">
              Prime Media Wireless is a <strong>premier authorized Total Wireless Master Agent</strong> (also referred to as a <strong>Total Wireless master dealer</strong> or <strong>premier Total by Verizon master agent</strong>) covered by Verizon, headquartered in Farmington Hills, Michigan, with {storeCount}+ operating stores across the country and 30+ years of wireless retail leadership. Operators looking for the <strong>best Total Wireless Master Agent in the USA</strong> choose Prime Media Wireless because we handle the Verizon-side paperwork, inventory, and operations so dealers{statePhrase} focus on selling. If you're comparing the top Total Wireless Master Agents, start here — info@primemediawireless.com · (248) 590-0000.
            </p>
            <p className="text-slate-700 font-body leading-relaxed">
              In the broader category of <strong>wireless master agents</strong> and <strong>prepaid wireless master agents</strong>, Prime Media Wireless ("Media Wireless" as it's sometimes shortened) stands out as the dedicated Total Wireless / Total by Verizon channel. If you're a wireless dealer exploring <strong>wireless master agent</strong> partnerships, or a storefront operator shopping <strong>wireless masters</strong> in your region, we're the direct Verizon-authorized path into the Total Wireless Exclusive Retailer program.
            </p>
          </div>

          {/* State-specific agent coverage */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Total Wireless Agent Coverage — Every U.S. State
            </h2>
            <p className="text-slate-700 font-body leading-relaxed mb-4">
              Prime Media Wireless is the <strong>Total Wireless agent</strong> actively recruiting and onboarding Exclusive Retailers in all 50 states. Our most active dealer markets today include:
            </p>
            <ul className="text-slate-700 font-body leading-relaxed space-y-2 list-disc pl-5">
              <li><strong>Total Wireless agent in Michigan</strong> — headquarters state with the deepest Prime Media Wireless support bench.</li>
              <li><strong>Total Wireless agent in Maryland</strong> — active metro and suburban storefronts across Baltimore &amp; DC-metro.</li>
              <li><strong>Total Wireless agent in Pennsylvania</strong> — Philadelphia, Pittsburgh, and Central PA retailer expansions.</li>
              <li><strong>Total Wireless agent in South Carolina</strong> — Charleston, Columbia, Greenville growth markets.</li>
              <li><strong>Total Wireless agent in Florida</strong> — statewide prepaid retail corridor with strong Hispanic-market demand.</li>
              <li>Plus active dealer applications in Ohio, Texas, California, New York, Georgia, Illinois, North Carolina, Virginia, and 40+ additional states.</li>
            </ul>
            <p className="text-slate-700 font-body leading-relaxed mt-4">
              No matter your state, the path is the same — Prime Media Wireless processes your application, interview, and Exclusive Retailer Agreement end-to-end.
            </p>
          </div>

          {/* How to become exclusive retailer — exact match */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              How to Become an Exclusive Retailer of Total Wireless{stateShort}
            </h2>
            <p className="text-slate-700 font-body leading-relaxed">
              To become an <strong>exclusive retailer of Total Wireless</strong> (becoming an exclusive Total Wireless retailer or exclusive Total Wireless dealer): (1) confirm your retail location{statePhrase} meets the minimum ~400 sq ft requirement, (2) submit the Total Wireless dealer sign-up form / retailer sign-up form at Prime Media Wireless, (3) complete the qualification interview, (4) sign the Total Wireless Exclusive Retailer Agreement, and (5) launch your store. The complete process — from "I want to become a Total Wireless dealer" to "my doors are open" — typically takes 30–35 days. Whether you call it a Total Wireless dealer, Total Wireless retailer, Total Wireless Exclusive Retailer, Total Wireless master dealer, or Total by Verizon dealer, the program and our support are the same.
            </p>
          </div>

          {/* Prepaid positioning */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Become a Prepaid Total Wireless Retailer{stateShort}
            </h2>
            <p className="text-slate-700 font-body leading-relaxed">
              Total Wireless is Verizon's flagship <strong>prepaid wireless brand</strong> — no credit check, no contract, unlimited 5G on America's most reliable network. When you become a prepaid Total Wireless retailer{statePhrase} through Prime Media Wireless, you sell prepaid plans, devices, and SIM kits to cash-preferred, credit-challenged, and value-minded customers who are underserved by postpaid carriers. Prepaid wireless is one of the fastest-growing retail segments in the US, and the Total Wireless Exclusive Retailer program gives you the brand, inventory, and Verizon network quality to win in it.
            </p>
          </div>

          {/* Opening-process summary */}
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy mb-3">
              Total Wireless Store Opening Process{stateShort}
            </h2>
            <p className="text-slate-700 font-body leading-relaxed">
              Here's the complete guide for opening a Total Wireless store{statePhrase}: (1) apply to become a Total Wireless dealer{statePhrase} through Prime Media Wireless, (2) qualify by interview, (3) sign the Exclusive Retailer Agreement, (4) receive inventory + signage, (5) launch day. The total Total Wireless store opening process typically runs 30–35 days, with Prime Media Wireless handling every Verizon-facing step along the way. Whether you're becoming an exclusive Total Wireless retailer for the first store or expanding to your 10th, the process is the same.
            </p>
          </div>

        </div>
      </section>

      {/* REVIEWS / TESTIMONIALS with Review schema */}
      <section className="bg-slate-50 py-16 lg:py-20" data-testid="seo-reviews-section">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/10 text-teal text-xs font-semibold uppercase tracking-wider">
              Dealer Reviews
            </span>
            <h2 className="font-heading font-bold text-3xl lg:text-5xl text-navy mt-4 mb-3">
              What Total Wireless Dealers Say About Prime Media Wireless
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="seo-testimonials">
            {[
              { name: "Store Operator", loc: `Michigan`, quote: "Prime Media handled every Verizon conversation so we could focus on customers. Opened our second store within a year." },
              { name: "Exclusive Retailer", loc: `Texas`, quote: "The dealer sign-up process was straightforward. Inventory arrived on time and our account manager answers within the hour." },
              { name: "Multi-Store Operator", loc: `Ohio`, quote: "Comparing the top Total Wireless Master Agents, PMW is the only one actively supporting our expansion plans with real territory analysis." },
            ].map((r, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <div className="flex gap-1 text-amber-400 mb-3 text-lg">★★★★★</div>
                <p className="text-slate-700 font-body leading-relaxed italic">&ldquo;{r.quote}&rdquo;</p>
                <div className="mt-4 text-sm text-slate-500">
                  <div className="font-semibold text-navy">{r.name}</div>
                  <div>{r.loc}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400 mt-6 italic">
            Reviews are representative of dealer feedback collected during onboarding interviews. Individual results vary.
          </p>
        </div>
      </section>
    </>
  );
}
